<template>
  <div class="landing-container">
    <!-- Video Background -->
    <video autoplay muted loop playsinline class="video-bg">
      <source src="/assets/vde-game.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    <div class="content">
      <!-- ส่วนแรก: ให้ผู้ใช้กรอกโค้ด -->
      <div v-if="!isCouponCodeSubmitted">
        <h1 class="title">สุ่มแจกเครดิต รับเพี่ม</h1>
        <h1 class="title animate-game-text">Phoenix</h1>
        <h1 class="title mb-5">ให้โชค!</h1>

        <div class="mb-6 flex flex-col gap-4 items-center">
          <input
            v-model="couponCode"
            type="text"
            placeholder="กรอก Code ของคุณ"
            class="px-4 py-2 rounded-md border w-80 text-center"
          />
          <img
            src="/assets/egg/next2.png"
            @click="submitCouponCode"
            :class="{ 'opacity-50 pointer-events-none': !couponCode }"
            class="action-btn"
            alt="submit coupon code"
          />
        </div>
      </div>

      <!-- ส่วนที่สอง: แสดงปุ่มสุ่ม เมื่อกรอกโค้ดแล้ว -->
      <div v-else>
        <div class="text-center mt-8 mb-4">
          <h1 class="title">กดที่ปุ่ม เริ่มเล่น</h1>
          <h1 class="title animate-game-text">เพื่อรับเครดิตเพี่ม</h1>
        </div>

        <div class="grid">
          <div
            v-for="(ticket, index) in tickets"
            :key="index"
            class="ticket"
            :class="{ shaking: index === selectedIndex }"
          >
            <img :src="`/assets/egg/${ticket.image}.png`" class="ticket-image neon" alt="ticket" />
          </div>
        </div>

        <!-- ปุ่มเริ่มเล่น -->
        <div class="w-full text-center mt-10">
          <img
            src="/assets/egg/start2.png"
            @click="startRandomReveal"
            :class="{ 'opacity-50 pointer-events-none': isRevealing }"
            class="next-btn inline-block"
            alt="start-button"
          />
        </div>
      </div>
    </div>

    <!-- Overlay สำหรับคลิป open-box2.mp4 -->
    <div v-if="showIntroVideo" class="intro-overlay">
      <video autoplay muted playsinline class="intro-video" @ended="onIntroEnded">
        <source src="/assets/open-box3.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>

    <!-- Overlay สำหรับคลิป final-box.mp4 และแสดงข้อความแจกรางวัล -->
    <div
      v-if="showFinalVideo"
      ref="finalOverlayRef"
      class="final-overlay"
      :class="{ show: showFinalVideo }"
    >
      <video autoplay muted loop playsinline class="final-video">
        <source src="/assets/vde-game.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <canvas id="confetti"></canvas>
      <div class="final-text animate-final-text">
        <p class="text-lg font-semibold">ยินดีด้วย! คุณได้รับรางวัล</p>
        <h2 class="title text-3xl font-bold mt-5">{{ rewardAmount }}฿</h2>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import axios from 'axios';
import { API_PATHS } from '@/utils/ApiPath.js';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

export default {
  name: 'CouponLandingPage',
  setup() {
    // สเตตโค้ด
    const couponCode = ref('');
    const isCouponCodeSubmitted = ref(false);

    // สเตตการสุ่ม
    const isRevealing = ref(false);
    const selectedIndex = ref(null);

    // สเตตการแสดงวิดีโอ
    const showIntroVideo = ref(false);
    const showFinalVideo = ref(false);
    const finalOverlayRef = ref(null);

    // รางวัลที่ได้
    const rewardAmount = ref(0);

    // ใส่ ticket placeholder 6 ใบ
    const tickets = ref(
      Array(6)
        .fill(null)
        .map(() => ({ image: 1 }))
    );

    // ฟังก์ชันแปลง error message จากเซิร์ฟเวอร์เป็นภาษาไทย
    const translateErrorMessage = (msg) => {
      if (!msg) return 'โค้ดนี้ไม่สามารถใช้ได้';

      // ตัวอย่างการ map ข้อความจาก backend -> ไทย
      if (msg === 'Code already used') {
        return 'โค้ดนี้ถูกใช้ไปแล้ว';
      }
      if (msg === 'Code not found') {
        return 'ไม่พบโค้ด';
      }
      // fallback
      return msg;
    };

    // ฟังก์ชันสำหรับเช็กโค้ดด้วย API CHECK_CODE
    const submitCouponCode = async () => {
      if (!couponCode.value) {
        Swal.fire({
          icon: 'warning',
          title: 'กรุณากรอกโค้ดก่อน',
          confirmButtonText: 'ตกลง',
        });
        return;
      }
      try {
        // เรียก API CHECK_CODE เพื่อตรวจสอบสถานะของโค้ด
        await axios.post(API_PATHS.CHECK_CODE, { code: couponCode.value });
        // ถ้าไม่มี error แปลว่าโค้ดยังไม่ถูกใช้
        isCouponCodeSubmitted.value = true;
      } catch (error) {
        const errMsg = translateErrorMessage(error.response?.data?.message);
        Swal.fire({
          icon: 'warning',
          title: errMsg,
          confirmButtonText: 'ตกลง',
        });
        // ยังคงให้ผู้ใช้กรอกโค้ดใหม่
      }
    };

    // ฟังก์ชันสำหรับ redeem โค้ด (ใช้ API USE_CODE)
    const redeemCouponCode = async () => {
      try {
        const response = await axios.post(API_PATHS.USE_CODE, {
          code: couponCode.value,
        });
        // ถ้าสำเร็จ (status 200) => ได้ข้อมูล reward
        rewardAmount.value = response.data.reward.amount;
      } catch (error) {
        console.error('Error redeeming coupon code:', error);
        const errMsg = translateErrorMessage(error.response?.data?.message);
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถใช้โค้ดได้',
          text: errMsg,
          confirmButtonText: 'ตกลง',
        });
        // รีเซ็ต state กลับไปให้ผู้ใช้กรอกโค้ดใหม่
        isCouponCodeSubmitted.value = false;
        return false;
      }
      return true;
    };

    // ฟังก์ชันสุ่ม
    const startRandomReveal = () => {
      if (isRevealing.value) return;
      isRevealing.value = true;
      let duration = 3000;
      let interval = 200;
      let timeElapsed = 0;
      let index = 0;

      const shuffleAnimation = setInterval(() => {
        selectedIndex.value = index;
        index = (index + 1) % tickets.value.length;
        timeElapsed += interval;

        if (timeElapsed >= duration) {
          clearInterval(shuffleAnimation);
          isRevealing.value = false;
          // สุ่มตำแหน่งสุดท้าย
          selectedIndex.value = Math.floor(Math.random() * tickets.value.length);

          // เมื่อสุ่มเสร็จ -> เรียก redeem โค้ด
          redeemCouponCode().then((success) => {
            if (success) {
              showIntroVideo.value = true;
            }
          });
        }
      }, interval);
    };

    // เมื่อ open-box2.mp4 จบ -> เล่น final overlay
    const onIntroEnded = async () => {
      showIntroVideo.value = false;
      showFinalVideo.value = true;

      await nextTick();
      finalOverlayRef.value?.classList.add('show');

      setTimeout(() => {
        initConfetti();
      }, 100);
    };

    // ฟังก์ชันสำหรับ confetti
    const initConfetti = () => {
      const confettiCanvas = document.getElementById('confetti');
      if (!confettiCanvas) return;
      const ctx = confettiCanvas.getContext('2d');
      confettiCanvas.width = window.innerWidth;
      confettiCanvas.height = window.innerHeight;
      const confettis = [];
      const colors = ['#FF007A', '#7A00FF', '#00FF7A', '#FFD700', '#00D4FF'];

      function createConfetti() {
        const confetti = {
          x: Math.random() * confettiCanvas.width,
          y: Math.random() * confettiCanvas.height - confettiCanvas.height,
          size: Math.random() * 10 + 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          speedX: Math.random() * 3 - 1.5,
          speedY: Math.random() * 5 + 2,
          rotation: Math.random() * 360,
        };
        confettis.push(confetti);
      }

      for (let i = 0; i < 200; i++) {
        createConfetti();
      }

      function animateConfetti() {
        ctx.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
        confettis.forEach((confetti, index) => {
          confetti.x += confetti.speedX;
          confetti.y += confetti.speedY;
          confetti.rotation += confetti.speedX;
          ctx.save();
          ctx.translate(confetti.x, confetti.y);
          ctx.rotate((confetti.rotation * Math.PI) / 180);
          ctx.fillStyle = confetti.color;
          ctx.fillRect(-confetti.size / 2, -confetti.size / 2, confetti.size, confetti.size);
          ctx.restore();
          if (confetti.y > confettiCanvas.height) {
            confettis.splice(index, 1);
          }
        });
        if (confettis.length > 0) {
          requestAnimationFrame(animateConfetti);
        }
      }

      animateConfetti();
    };

    onMounted(() => {
      window.addEventListener('resize', () => {
        const confettiCanvas = document.getElementById('confetti');
        if (confettiCanvas) {
          confettiCanvas.width = window.innerWidth;
          confettiCanvas.height = window.innerHeight;
        }
      });
    });

    return {
      couponCode,
      isCouponCodeSubmitted,
      isRevealing,
      selectedIndex,
      showIntroVideo,
      showFinalVideo,
      finalOverlayRef,
      rewardAmount,
      tickets,
      submitCouponCode,
      startRandomReveal,
      onIntroEnded,
    };
  },
};
</script>

<style scoped>
/* (สไตล์เหมือนเดิม) */
.landing-container {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.video-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  z-index: -1;
  transform: translate(-50%, -50%);
  object-fit: cover;
}

.content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.title {
  font-size: 2rem;
  font-weight: bold;
  color: #ffd700;
  text-shadow: 2px 2px 5px #000;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, 100px);
  gap: 15px;
}

.ticket {
  position: relative;
}
.ticket-image {
  width: 80px;
  height: auto;
  object-fit: contain;
  transition: transform 0.2s ease-in-out;
}

.ticket.shaking .ticket-image {
  animation: shake 0.15s infinite alternate;
}

.ticket:hover .ticket-image {
  transform: scale(1.1);
}

.neon {
  filter: drop-shadow(0 0 2px #ffea00) drop-shadow(0 0 8px #ffea00) drop-shadow(0 0 12px #ffea00);
}

@keyframes shake {
  from {
    transform: translateY(-5px);
  }
  to {
    transform: translateY(5px);
  }
}

@keyframes gameTextEffect {
  0% {
    transform: scale(1);
    text-shadow: 0 0 5px #ffd700, 0 0 10px #ff8c00;
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    text-shadow: 0 0 15px #ffd700, 0 0 30px #ff8c00;
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    text-shadow: 0 0 5px #ffd700, 0 0 10px #ff8c00;
    opacity: 1;
  }
}

.animate-game-text {
  animation: gameTextEffect 1.5s ease-in-out infinite;
}

.intro-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.intro-video {
  width: 100%;
  height: auto;
  object-fit: cover;
}

.final-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.final-video {
  width: 100%;
  height: auto;
  object-fit: cover;
}

#confetti {
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1000;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-final-text {
  animation: fadeInUp 1s ease-out forwards;
}

.final-text {
  position: absolute;
  top: 28%;
  left: 26%;
  transform: translate(-50%, -50%);
  color: #ffd700;
  text-align: center;
  text-shadow: 2px 2px 5px #000;
  z-index: 10000;
}

.final-text .text-lg {
  font-size: 1.25rem;
}

.final-text .text-3xl {
  font-size: 4.875rem;
}

.action-btn {
  width: 100px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.3s ease;
}

.action-btn:hover {
  transform: scale(1.05);
}

.next-btn {
  width: 150px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.3s ease;
}

.next-btn:hover {
  transform: scale(1.05);
}
</style>
